import {Injectable} from "@angular/core";
import {APIRequestResources, CachedAPIRequest, PaginationResponse} from "../../core";
import {BehaviorSubject, catchError, Observable, Subject, take, tap} from "rxjs";
import {toSignal} from "@angular/core/rxjs-interop";
import {HttpClient} from "@angular/common/http";
import {Router} from "@angular/router";
import {handleError} from "../../core/util/util";
import {SalesOwnerData, SalesOwnerDTO} from "../interfaces/sales-owner.entity";

@Injectable({
    providedIn: 'root'
})
export class SalesOwnerService extends CachedAPIRequest {

    private readonly $all = new BehaviorSubject<SalesOwnerDTO[]>([]);
    all = toSignal(this.$all, {initialValue: []})

    private instanceActiveValues = new Map<string, BehaviorSubject<SalesOwnerData | undefined>>();

    private dropdownStates = new Map<string, boolean>();

    private dropdownStateChanged = new Subject<{ instanceId: string, isOpen: boolean }>();
    dropdownState$ = this.dropdownStateChanged.asObservable();

    constructor(protected override http: HttpClient, private router: Router) {
        super(http, APIRequestResources.SalesOwnerService)
        this.find({
            sales_owner_name: '',
            status: -1,
            items_per_page: -1,
            page_number: 1
        }, true).pipe(take(1)).subscribe()
    }

    find(params: any, refresh = false) {
        return this.get<PaginationResponse<SalesOwnerDTO[]>>({
            endpoint: `find`,
            params: params
        }, refresh ? 'freshness' : 'performance').pipe(
            tap(res => this.$all.next(res.data.data ?? [])),
            catchError(handleError)
        )
    }

    getById(id: string, instanceId: string, refresh = false) {
        return this.get<SalesOwnerData>({id}, refresh ? 'freshness' : 'performance')
            .pipe(
                tap((res) => {
                    this.setActiveForInstance(instanceId, res.data);
                }),
                catchError(handleError)
            )
    }

    registerDropdown(instanceId: string) {
        this.dropdownStates.set(instanceId, false);
        if (!this.instanceActiveValues.has(instanceId)) {
            this.instanceActiveValues.set(instanceId, new BehaviorSubject<SalesOwnerData | undefined>(undefined));
        }
    }

    unregisterDropdown(instanceId: string) {
        this.dropdownStates.delete(instanceId);
        this.instanceActiveValues.delete(instanceId);
    }

    getActiveForInstance(instanceId: string): Observable<SalesOwnerData | undefined> {
        if (!this.instanceActiveValues.has(instanceId)) {
            this.instanceActiveValues.set(instanceId, new BehaviorSubject<SalesOwnerData | undefined>(undefined));
        }
        return this.instanceActiveValues.get(instanceId)!.asObservable();
    }

    setActiveForInstance(instanceId: string, value: SalesOwnerData | undefined) {
        if (!this.instanceActiveValues.has(instanceId)) {
            this.instanceActiveValues.set(instanceId, new BehaviorSubject<SalesOwnerData | undefined>(undefined));
        }
        this.instanceActiveValues.get(instanceId)!.next(value);
    }

    closeAllDropdownsExcept(instanceId: string) {
        this.dropdownStateChanged.next({instanceId, isOpen: true});
    }

    isDropdownOpen(instanceId: string): boolean {
        return this.dropdownStates.get(instanceId) || false;
    }

    setDropdownState(instanceId: string, isOpen: boolean) {
        this.dropdownStates.set(instanceId, isOpen);
    }

    initial = () => {
        this.instanceActiveValues.forEach((subject) => subject.next(undefined));
        this.$all.next([]);
    }
}